import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { formatBytes } from '../utilities/general'
import { type DirectionalTraffic } from '../models/useUsage'

// A matrix: rows = receive / transmit, columns = relay / proxy / storage, with a Total row and Total
// column of marginal sums. Billing now charges the node's total traffic — every leg — so the
// bottom-right grand total (receive + transmit across all sockets) IS the billable bandwidth.
// `caption` fills the otherwise-empty top-left corner (callers pass the period, e.g. "This month").
export default function TrafficMatrix({ traffic, caption }: Readonly<{ traffic: DirectionalTraffic; caption?: string }>) {
  const cell = { border: 0, py: 0.5, px: 1 }
  const receiveTotal = traffic.relayReceive + traffic.proxyReceive + traffic.storageReceive
  const transmitTotal = traffic.relayTransmit + traffic.proxyTransmit + traffic.storageTransmit
  const num = (v: number, emphasis = false) => (
    <TableCell align="right" sx={{ ...cell, color: 'contrast.max', whiteSpace: 'nowrap', fontWeight: emphasis ? 600 : 400 }}>
      {formatBytes(v)}
    </TableCell>
  )
  const head = (label: string) => (
    <TableCell align="right" sx={{ ...cell, color: 'text.secondary' }}>
      {label}
    </TableCell>
  )
  const rowLabel = (label: string) => (
    <TableCell sx={{ ...cell, color: 'text.secondary' }}>{label}</TableCell>
  )
  // ⚠ A TABLE IN A DIALOG NEEDS ITS OWN HORIZONTAL SCROLL — `TableContainer` is that scroll. At phone width the usage panel's paper
  // is about 310 px and these columns want more; without the wrapper the row overflows the card
  // and the Total column is clipped, with no way to reach it.
  return (
    <TableContainer>
      <Table size="small" sx={{ width: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...cell, color: 'text.secondary', fontWeight: 400, whiteSpace: 'nowrap' }}>{caption}</TableCell>
            {head('Relay')}
            {head('Proxy')}
            {head('Storage')}
            {head('Total')}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {rowLabel('Receive')}
            {num(traffic.relayReceive)}
            {num(traffic.proxyReceive)}
            {num(traffic.storageReceive)}
            {num(receiveTotal)}
          </TableRow>
          <TableRow>
            {rowLabel('Transmit')}
            {num(traffic.relayTransmit)}
            {num(traffic.proxyTransmit)}
            {num(traffic.storageTransmit)}
            {num(transmitTotal)}
          </TableRow>
          <TableRow>
            {rowLabel('Total')}
            {num(traffic.relayReceive + traffic.relayTransmit)}
            {num(traffic.proxyReceive + traffic.proxyTransmit)}
            {num(traffic.storageReceive + traffic.storageTransmit)}
            {num(receiveTotal + transmitTotal, true)}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}
